import { inject } from 'inversify';
import { Injectable } from '../../decorators/injectable';
import { HttpService, type IHttpService } from '../http/http.service';

// Tipi record olio (sincronizzato con backend)
export type OilRecordType = 'OIL_NOT_REPLACED' | 'OIL_REPLACED' | 'OIL_REGENERATED';

// Costanti HACCP per TPM (Total Polar Materials)
export const HACCP_TPM_MAX = 25; // Limite massimo 25% (non conforme oltre)
export const HACCP_TPM_WARNING = 24; // Soglia di attenzione: 24-25% in zona di attenzione

export interface Fryer {
  id: string;
  name: string;
  icon: string;
  description: string | null;
  isActive: boolean;
}

export interface OilRecord {
  id: string;
  fryerId: string;
  fryerName: string;
  date: string;
  completedAt: string;
  recordType: OilRecordType;
  tpmValue: number | null; // Normalizzato da tpiValue del backend
  notes: string | null;
  performedBy: string | null;
  performerName: string;
  signatureStorageKey: string | null;
}

interface CreateOilRecordRequest {
  date: string;
  recordType: OilRecordType;
  tpiValue?: number;
  notes?: string;
}

export interface CorrectOilRecordRequest {
  recordType: OilRecordType;
  tpiValue?: number;
  notes?: string;
  reason?: string;
}

export interface SignOilRecordBatchRequest {
  records: Array<{
    fryerId: string;
    date: string;
    recordType: OilRecordType;
    tpiValue?: number;
    notes?: string;
  }>;
  signatureImage: string;
}

interface SignOilRecordBatchResult {
  records: OilRecord[];
  /** Fryer IDs skipped because already registered for the day. */
  skippedFryers: string[];
}

interface TpmValidationResult {
  isCompliant: boolean;
  tpmValue: number;
  message: string;
  status: 'success' | 'warning' | 'error';
}

export interface CreateFryerRequest {
  name: string;
  icon: string;
  description?: string;
  isActive?: boolean;
}

export interface UpdateFryerRequest {
  name?: string;
  icon?: string;
  description?: string;
  isActive?: boolean;
}

type OilDayStatus = 'complete' | 'partial' | 'nonCompliant' | 'missing' | 'future' | 'notRequired';

interface OilCalendarDayDetail {
  status: OilDayStatus;
  totalFryers: number;
  completedFryers: number;
  /** Oil records dated that day: oil changes are on demand, nothing is planned. */
  recordCount: number;
}

interface OilCalendarResponse {
  days: Record<string, OilCalendarDayDetail>;
}

export interface IFryerService {
  findAll(): Promise<Fryer[]>;
  findActive(): Promise<Fryer[]>;
  findById(id: string): Promise<Fryer>;
  create(dto: CreateFryerRequest): Promise<Fryer>;
  update(id: string, dto: UpdateFryerRequest): Promise<Fryer>;
  delete(id: string): Promise<void>;
  findOilRecords(fryerId: string, startDate?: string, endDate?: string): Promise<OilRecord[]>;
  createOilRecord(fryerId: string, dto: CreateOilRecordRequest): Promise<OilRecord>;
  correctOilRecord(recordId: string, dto: CorrectOilRecordRequest): Promise<OilRecord>;
  signBatch(request: SignOilRecordBatchRequest): Promise<SignOilRecordBatchResult>;
  validateTpm(tpmValue: number): TpmValidationResult;
  getOilCalendar(from: string, to: string): Promise<OilCalendarResponse>;
}

@Injectable()
class FryerService implements IFryerService {
  constructor(@inject(HttpService) private readonly httpService: IHttpService) {}

  validateTpm(tpmValue: number): TpmValidationResult {
    let isCompliant: boolean;
    let message: string;
    let status: 'success' | 'warning' | 'error';

    const formattedTpm = tpmValue.toFixed(2);

    // Boundary aligned with the backend runtime source of truth
    // (HaccpValidatorService.validateOil): <=24 conforme, 24<x<=25 attenzione,
    // >25 non conforme. The lower band is inclusive on the "ok" side.
    if (tpmValue <= HACCP_TPM_WARNING) {
      isCompliant = true;
      status = 'success';
      message = `${formattedTpm}% (≤ ${HACCP_TPM_WARNING}%)`;
    } else if (tpmValue <= HACCP_TPM_MAX) {
      isCompliant = true;
      status = 'warning';
      message = `${formattedTpm}% (${HACCP_TPM_WARNING}% - ${HACCP_TPM_MAX}%)`;
    } else {
      isCompliant = false;
      status = 'error';
      message = `${formattedTpm}% (> ${HACCP_TPM_MAX}% - NON CONFORME)`;
    }

    return {
      isCompliant,
      tpmValue,
      message,
      status,
    };
  }

  private mapToOilRecord(response: {
    id: string;
    fryerId: string;
    fryerName: string;
    date: string;
    completedAt: string;
    recordType: string;
    tpiValue: number | null;
    notes: string | null;
    performedBy: string | null;
    performerName: string;
    signatureStorageKey: string | null;
  }): OilRecord {
    return {
      id: response.id,
      fryerId: response.fryerId,
      fryerName: response.fryerName,
      date: response.date,
      completedAt: response.completedAt,
      recordType: response.recordType as OilRecordType,
      tpmValue: response.tpiValue, // Normalizziamo da tpiValue a tpmValue
      notes: response.notes,
      performedBy: response.performedBy,
      performerName: response.performerName,
      signatureStorageKey: response.signatureStorageKey,
    };
  }

  async findAll(): Promise<Fryer[]> {
    return this.httpService.get<Fryer[]>('fryer');
  }

  async findActive(): Promise<Fryer[]> {
    return this.httpService.get<Fryer[]>('fryer/active');
  }

  async findById(id: string): Promise<Fryer> {
    return this.httpService.get<Fryer>(`fryer/${id}`);
  }

  async create(dto: CreateFryerRequest): Promise<Fryer> {
    return this.httpService.post<Fryer>('fryer', dto);
  }

  async update(id: string, dto: UpdateFryerRequest): Promise<Fryer> {
    return this.httpService.put<Fryer>(`fryer/${id}`, dto);
  }

  async delete(id: string): Promise<void> {
    await this.httpService.delete(`fryer/${id}`);
  }

  async findOilRecords(
    fryerId: string,
    startDate?: string,
    endDate?: string,
  ): Promise<OilRecord[]> {
    const params = new URLSearchParams();
    if (startDate) {
      params.set('startDate', startDate);
    }
    if (endDate) {
      params.set('endDate', endDate);
    }

    const queryString = params.toString();
    const url = queryString
      ? `fryer/${fryerId}/oil-record?${queryString}`
      : `fryer/${fryerId}/oil-record`;

    const records =
      await this.httpService.get<
        Array<{
          id: string;
          fryerId: string;
          fryerName: string;
          date: string;
          completedAt: string;
          recordType: string;
          tpiValue: number | null;
          notes: string | null;
          performedBy: string | null;
          performerName: string;
          signatureStorageKey: string | null;
        }>
      >(url);

    return records.map((r) => this.mapToOilRecord(r));
  }

  async createOilRecord(fryerId: string, dto: CreateOilRecordRequest): Promise<OilRecord> {
    const response = await this.httpService.post<{
      id: string;
      fryerId: string;
      fryerName: string;
      date: string;
      completedAt: string;
      recordType: string;
      tpiValue: number | null;
      notes: string | null;
      performedBy: string | null;
      performerName: string;
      signatureStorageKey: string | null;
    }>(`fryer/${fryerId}/oil-record`, dto);

    return this.mapToOilRecord(response);
  }

  async correctOilRecord(recordId: string, dto: CorrectOilRecordRequest): Promise<OilRecord> {
    const response = await this.httpService.post<{
      id: string;
      fryerId: string;
      fryerName: string;
      date: string;
      completedAt: string;
      recordType: string;
      tpiValue: number | null;
      notes: string | null;
      performedBy: string | null;
      performerName: string;
      signatureStorageKey: string | null;
    }>(`fryer/oil-records/${recordId}/corrections`, dto);

    return this.mapToOilRecord(response);
  }

  async signBatch(request: SignOilRecordBatchRequest): Promise<SignOilRecordBatchResult> {
    const response = await this.httpService.post<{
      records: Array<{
        id: string;
        fryerId: string;
        fryerName: string;
        date: string;
        completedAt: string;
        recordType: string;
        tpiValue: number | null;
        notes: string | null;
        performedBy: string | null;
        performerName: string;
        signatureStorageKey: string | null;
      }>;
      skippedFryers: string[];
    }>('fryer/oil-record-batch', request);

    return {
      records: response.records.map((r) => this.mapToOilRecord(r)),
      skippedFryers: response.skippedFryers ?? [],
    };
  }

  async getOilCalendar(from: string, to: string): Promise<OilCalendarResponse> {
    return this.httpService.get<OilCalendarResponse>(`fryer/oil-calendar?from=${from}&to=${to}`);
  }
}

export { FryerService };
